import {
  doc,
  runTransaction,
  type Firestore,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import type { CanvasElementType } from '../types/schema'
import { logAuditEvent } from './auditLedgerService'
import { type AuditCategory } from './auditCategories'
import { enqueueDeferredEvent, type DeferredWorkspaceActivityPayload } from './deferredEventOutbox'
import { firebaseAuth, firebaseFunctions } from './firebase'

export const AUDIT_ACTIVITY_TYPES = ['CREATE', 'UPDATE', 'DELETE', 'MOVE', 'SUBMIT', 'SCHEDULE', 'UPLOAD', 'EXPORT', 'SYNC', 'REVOKE_AUTHORIZATION', 'INVITE', 'ROLE_CHANGE', 'COMMAND_EXECUTION', 'SECURITY_ANOMALY', 'EXPORT_RAW_CSV', 'OTHER'] as const
export type AuditActivityType = (typeof AUDIT_ACTIVITY_TYPES)[number]

export type WorkspaceActivityRecord = {
  id: string
  workspaceId: string
  actorId: string
  actorName: string
  targetType: string
  targetId: string
  targetTitle: string
  action: string
  activityType: AuditActivityType
  summary: string
  createdAt: Date | null
  metadata: Record<string, unknown> | null
  actionType: string
  nodeType: string
  maskedPayload: Record<string, unknown> | null
  snapshot: Record<string, unknown> | null
  auditCategory: AuditCategory
}

export type AuditActivityFilters = {
  from: Date
  to: Date
  memberIds: string[]
  itemIds: string[]
  activityTypes: AuditActivityType[]
  entityTypes?: string[]
  actionTypes?: string[]
  searchPrefix?: string
}

export type AuditActivityCursor = { createdAt: Date; id: string }

const toDate = (value: unknown): Date | null => {
  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') return value.toDate()
  if (typeof value === 'string') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return value instanceof Date ? value : null
}

export const normalizeAuditActivityType = (action: unknown): AuditActivityType => {
  const normalized = typeof action === 'string' ? action.toUpperCase() : ''
  if (normalized === 'SECURITY_ANOMALY' || normalized === 'EXPORT_RAW_CSV' || normalized === 'COMMAND_EXECUTION') return normalized
  if (normalized.includes('ROLE')) return 'ROLE_CHANGE'
  if (normalized.includes('INVITE') || normalized.includes('MEMBER_JOIN')) return 'INVITE'
  if (normalized.includes('DELETE') || normalized.includes('REMOVE') || normalized.includes('REVOKE')) return 'DELETE'
  if (normalized.includes('MOVE') || normalized.includes('RESTORE') || normalized.includes('ARCHIVE')) return 'MOVE'
  if (normalized.includes('CREATE') || normalized.includes('ADD')) return 'CREATE'
  if (
    normalized.includes('UPDATE') ||
    normalized.includes('EDIT') ||
    normalized.includes('COMPLETE') ||
    normalized.includes('REOPEN') ||
    normalized.includes('ASSIGN') ||
    normalized.includes('SAVE') ||
    normalized.includes('CHANGE') ||
    normalized.includes('DEPENDENCY') ||
    normalized.includes('PROOF') ||
    normalized.includes('STATUS')
  ) return 'UPDATE'
  return 'OTHER'
}
const matchesClientFilters = (activity: WorkspaceActivityRecord, filters: AuditActivityFilters) =>
  (filters.memberIds.length === 0 || filters.memberIds.includes(activity.actorId))
  && (filters.activityTypes.length === 0 || filters.activityTypes.includes(activity.activityType))
  && (!filters.entityTypes?.length || filters.entityTypes.includes(activity.nodeType))

export const queryWorkspaceActivityPage = async (
  db: Firestore,
  workspaceId: string,
  filters: AuditActivityFilters,
  cursor: AuditActivityCursor | null = null,
  pageSize = 50,
) => {
  void db
  await firebaseAuth.currentUser?.getIdToken()
  const callable = httpsCallable<{
    workspaceId: string
    filters: {
      from: string
      to: string
      memberIds: string[]
      itemIds: string[]
      activityTypes: string[]
      entityTypes: string[]
      actionTypes: string[]
      searchPrefix: string
    }
    cursor: { createdAt: string; id: string } | null
    pageSize: number
  }, { records: Array<WorkspaceActivityRecord & { createdAt: string | null }>; nextCursor: { createdAt: string; id: string } | null; hasMore: boolean }>(firebaseFunctions, 'getWorkspaceActivity')
  const result = (await callable({
    workspaceId,
    filters: {
      from: filters.from.toISOString(),
      to: filters.to.toISOString(),
      memberIds: filters.memberIds,
      itemIds: filters.itemIds,
      activityTypes: filters.activityTypes,
      entityTypes: filters.entityTypes ?? [],
      actionTypes: filters.actionTypes ?? [],
      searchPrefix: filters.searchPrefix ?? '',
    },
    cursor: cursor ? { createdAt: cursor.createdAt.toISOString(), id: cursor.id } : null,
    pageSize,
  })).data
  const records = result.records.map((entry) => ({ ...entry, createdAt: toDate(entry.createdAt) })).filter((entry) => matchesClientFilters(entry, filters))
  return {
    records,
    nextCursor: result.nextCursor ? { createdAt: toDate(result.nextCursor.createdAt) ?? new Date(), id: result.nextCursor.id } : null,
    hasMore: result.hasMore,
  }
}

export const subscribeWorkspaceActivityFirstPage = (
  db: Firestore,
  workspaceId: string,
  filters: AuditActivityFilters,
  onChange: (result: { records: WorkspaceActivityRecord[]; nextCursor: AuditActivityCursor | null; hasMore: boolean }) => void,
  onError?: (error: Error) => void,
  pageSize = 50,
) => {
  let cancelled = false
  void queryWorkspaceActivityPage(db, workspaceId, filters, null, pageSize)
    .then((result) => { if (!cancelled) onChange(result) })
    .catch((error) => { if (!cancelled) onError?.(error instanceof Error ? error : new Error('Could not load workspace activity.')) })
  return () => { cancelled = true }
}

export const queryAllWorkspaceActivity = async (db: Firestore, workspaceId: string, filters: AuditActivityFilters) => {
  const records: WorkspaceActivityRecord[] = []
  let cursor: AuditActivityCursor | null = null
  for (let page = 0; page < 200; page += 1) {
    const result = await queryWorkspaceActivityPage(db, workspaceId, filters, cursor, 250)
    records.push(...result.records)
    if (!result.hasMore || !result.nextCursor) break
    cursor = result.nextCursor
  }
  return records
}

export type WorkspaceActivityTargetType = CanvasElementType | 'CHECKLIST' | 'MEMBER' | 'INVITE' | 'CANVAS' | 'INVENTORY' | 'EXPENSE'
export type WorkspaceActivityAction =
  | 'CREATE'
  | 'UPDATE'
  | 'MOVE_BOARD'
  | 'DELETE'
  | 'RESTORE'
  | 'CHECKLIST_UPDATE'
  | 'CHECKLIST_CREATED'
  | 'CHECKLIST_UPDATED'
  | 'CHECKLIST_STATUS_CHANGED'
  | 'CHECKLIST_COMPLETED'
  | 'CHECKLIST_REOPENED'
  | 'CHECKLIST_DELETED'
  | 'CHECKLIST_DEPENDENCY_CHANGED'
  | 'CHECKLIST_ASSIGNED'
  | 'CHECKLIST_PROOF_UPDATED'
  | 'PROOF_VERIFIED'
  | 'CHECKLIST_FORCE_COMPLETED'
  | 'CHECKLIST_COMPLETION_BLOCKED'
  | 'INVITE_ACCEPTED'
  | 'MEMBER_ROLE_CHANGE'
  | 'CANVAS_SAVE'
  | 'ASSIGNMENT_CHANGE'
  | 'DEPENDENCY_CHANGE'
  | 'COMMENT_CREATE'
  | 'COMMENT_EDIT'
  | 'COMMENT_DELETE'
  | 'COMMENT_REACTION'
  | 'PROOF_UPDATE'
  | 'STATUS_CHANGE'
  | 'EXPORT_RAW_CSV'
  | 'SECURITY_ANOMALY'
  | 'COMMAND_EXECUTION'
  | 'RESOURCE_GATING_ERROR'
  | 'RISK_RESOLVED'
  | 'HELPER_BLOCKER_RAISED'
  | 'HELPER_REPLENISHMENT_REQUESTED'
  | 'STOCK_COUNT_SUBMITTED'
  | 'STOCK_COUNT_APPROVED'
  | 'STOCK_SCAN_SUGGESTED'
  | 'EXPENSE_APPROVED_FROM_INTAKE'

export const recordWorkspaceActivity = async (
  db: Firestore,
  input: {
    workspaceId: string
    actorId?: string | null
    actorName?: string | null
    targetType: WorkspaceActivityTargetType
    targetId: string
    targetTitle: string
    action: WorkspaceActivityAction
    summary: string
    board?: string | null
    metadata?: Record<string, unknown>
    deferredActionKey?: 'WORKSPACE_ACTIVITY' | 'UPLOAD_HUB_ACTIVITY'
  },
) => {
  if (!input.workspaceId || !input.actorId) return
  const queued = await enqueueDeferredEvent({
    actionKey: input.deferredActionKey ?? 'WORKSPACE_ACTIVITY',
    kind: 'WORKSPACE_ACTIVITY',
    payload: {
      workspaceId: input.workspaceId,
      actorId: input.actorId,
      actorName: input.actorName,
      targetType: input.targetType,
      targetId: input.targetId,
      targetTitle: input.targetTitle,
      action: input.action,
      summary: input.summary,
      board: input.board,
      metadata: input.metadata,
    } satisfies DeferredWorkspaceActivityPayload,
  })
  if (queued) return
  await runTransaction(db, async (transaction) => {
    const workspace = await transaction.get(doc(db, 'workspaces', input.workspaceId))
    logAuditEvent(db, transaction, workspace.data(), {
      workspaceId: input.workspaceId,
      actorId: input.actorId ?? '',
      actorName: input.actorName,
      nodeType: input.targetType,
      targetId: input.targetId,
      targetTitle: input.targetTitle,
      action: input.action,
      activityType: normalizeAuditActivityType(input.action),
      summary: input.summary,
      board: input.board,
      payload: input.metadata,
    })
  })
}
